import { useLazyQuery } from "@apollo/client";
import { compact } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  GetMeetingOverviewMeetingsQueryQuery,
  GetMeetingOverviewMeetingsQueryQueryVariables,
  MeetingDialogFragmentFragment,
} from "types/graphql-schema";

import Loading from "@components/loading/loading";
import DeprecatedSelect from "@components/select/select";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import { UserComboboxOption } from "@components/user-combobox/user-combobox-list";
import { classNames } from "@helpers/css";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import getMeetingOverviewMeetingsQuery from "../graphql/get-meeting-overview-meetings-query";
import { MeetingType } from "./meetings-overview-constants";
import OverviewMeetingList from "./overview-meeting-list";

const MeetingOverview = ({
  selectedUser,
  selectedDay,
  filterClassName,
  meetingListClassName,
  onClickMeetingLink,
}: {
  selectedDay: string;
  selectedUser: UserComboboxOption;
  filterClassName?: string;
  meetingListClassName?: string;
  onClickMeetingLink?: (meetingId: number, meetingGroupId: number) => void;
}) => {
  const todayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [initComplete, setInitComplete] = useState(false);
  const [loadMore, setLoadMore] = useState<"past" | "future" | "search" | null>(
    null,
  );
  const [meetings, setMeetings] = useState<MeetingDialogFragmentFragment[]>([]);
  const [participantFilter, setParticipantFilter] = useState<number | null>(
    null,
  );
  const [meetingTypeFilter, setMeetingTypeFilter] = useState(MeetingType.ALL);
  const isShowingWeekends = false;

  const [meetingDisplayedRange, setMeetingDisplayedRange] = useState<{
    start: string;
    end: string;
  }>({
    start: moment(selectedDay).startOf("isoWeek").format(),
    end: moment(selectedDay).startOf("isoWeek").add(1, "week").format(),
  });

  useEffect(() => {
    setMeetingDisplayedRange({
      start: moment(selectedDay).startOf("isoWeek").format(),
      end: moment(selectedDay).startOf("isoWeek").add(1, "week").format(),
    });
  }, [selectedDay]);

  const [fetchMeetings, { data, loading }] = useLazyQuery<
    GetMeetingOverviewMeetingsQueryQuery,
    GetMeetingOverviewMeetingsQueryQueryVariables
  >(getMeetingOverviewMeetingsQuery, {});
  const hasMoreSearchResults = data?.calendar?.pageInfo.hasNextPage;

  const handleReceiveMeetingsResponse = useCallback(
    (response: GetMeetingOverviewMeetingsQueryQuery) => {
      const meetings = compact(
        assertEdgesNonNull(response.calendar).map(
          (calendarItem) => calendarItem.meeting,
        ),
      );
      setMeetings((prev) => {
        const existingIds = prev.map(({ id }) => id);
        const updatedMeetings = prev.map((prevMeeting) => {
          if (meetings.map(({ id }) => id).includes(prevMeeting.id)) {
            return assertNonNull(
              meetings.find(({ id }) => id === prevMeeting.id),
            );
          }
          return prevMeeting;
        });
        const newMeetings = meetings.filter(
          ({ id }) => !existingIds.includes(id),
        );
        return [...updatedMeetings, ...newMeetings];
      });
      setInitComplete(true);
      setLoadMore(null);
    },
    [],
  );

  useEffect(() => {
    if (initComplete) {
      const scrollInterval = setTimeout(() => {
        if (todayRef.current && scrollContainerRef.current) {
          const scrollTop = todayRef.current.offsetTop;
          scrollContainerRef.current.scrollTo({
            top: scrollTop - 10,
            behavior: "smooth",
          });
        }
      }, 700);
      return () => clearTimeout(scrollInterval);
    }
    // scroll to today only on page load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initComplete]);

  useEffect(() => {
    if (loadMore === null && initComplete) {
      return;
    }
    const newStart = !initComplete
      ? meetingDisplayedRange.start
      : loadMore === "past"
        ? moment(meetingDisplayedRange.start).subtract(1, "week").format()
        : meetingDisplayedRange.end;

    fetchMeetings({
      variables: {
        forUserId: selectedUser.id as number,
        dateRangeStart: newStart,
        dateRangeEnd: moment(newStart).add(1, "week").format(),
        orderBy: "start_datetime",
        first: null,
      },
      onCompleted: (response) => {
        handleReceiveMeetingsResponse(response);
        if (loadMore === "past") {
          setMeetingDisplayedRange((prev) => ({
            ...prev,
            start: newStart,
          }));
        } else if (loadMore === "future") {
          setMeetingDisplayedRange((prev) => ({
            ...prev,
            end: moment(newStart).add(1, "week").format(),
          }));
        }
      },
    });
    // only want to fetch more when index changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMore, initComplete]);

  const {
    options: assessmentUserFilterOptions,
    query,
    setQuery,
  } = useUserComboboxQuery({});
  const userComboboxValue =
    assessmentUserFilterOptions.find(({ id }) => id === participantFilter) ||
    null;

  const handleChangeParticipantFilter = useCallback(
    (option?: UserComboboxOption) => {
      setParticipantFilter(
        option && typeof option.id === "number" ? option.id : null,
      );
    },
    [],
  );

  const handleMeetingUpdated = useCallback(
    (data: { meeting: MeetingDialogFragmentFragment }) => {
      if (data.meeting.startDatetime) {
        setMeetings((prev) => {
          if (prev.find((meeting) => meeting.id === data.meeting.id)) {
            return prev.map((meeting) => {
              if (meeting.id === data.meeting.id) {
                return data.meeting;
              }
              return meeting;
            });
          }
          return [...prev, data.meeting];
        });
      } else {
        // else remove from meetings list and update unscheduledMeetings
        setMeetings((prev) =>
          prev.filter((meeting) => meeting.id !== data.meeting.id),
        );
      }
    },
    [],
  );

  return (
    <div className="@container/overview-meetings h-full">
      <div className={classNames("sticky z-40 bg-white")}>
        <div
          className={classNames(
            "flex items-center justify-between gap-2 h-14 px-4 border-b",
            filterClassName,
          )}
        >
          <div className="">
            <UserCombobox
              onChangeValue={handleChangeParticipantFilter}
              options={assessmentUserFilterOptions}
              value={userComboboxValue}
              placeholder="All people"
              clearable={participantFilter !== null}
              query={query}
              onChangeQuery={setQuery}
              onClearValue={handleChangeParticipantFilter}
            />
          </div>
          <DeprecatedSelect
            value={meetingTypeFilter}
            options={[
              {
                value: MeetingType.ALL,
                label: "All meetings",
              },
              {
                value: MeetingType.ONE_ON_ONE,
                label: "1-on-1s",
              },
              {
                value: MeetingType.GROUP,
                label: "Group meetings",
              },
            ]}
            onChange={(opt) => setMeetingTypeFilter(opt.value)}
            aria-label="Meeting type filter"
          />
        </div>
      </div>

      <div
        className={classNames(
          "absolute inset-0 overflow-y-scroll px-6 pt-4 pb-12",
          "top-14",
          meetingListClassName,
        )}
        ref={scrollContainerRef}
      >
        {/* Main loader on first page hit */}
        {loading && !initComplete && loadMore !== "search" && (
          <Loading className="p-6" size={6}>
            Loading meetings...
          </Loading>
        )}

        {initComplete && (
          <OverviewMeetingList
            ref={todayRef}
            hasMoreSearchResults={hasMoreSearchResults}
            showSearchResults={false}
            meetings={meetings}
            loading={loading}
            participantFilter={participantFilter}
            meetingTypeFilter={meetingTypeFilter}
            meetingDisplayedRange={meetingDisplayedRange}
            onMeetingUpdated={handleMeetingUpdated}
            onClickMeetingLink={onClickMeetingLink}
            loadMore={loadMore}
            onChangeLoadMore={setLoadMore}
            isShowingWeekends={isShowingWeekends}
          />
        )}

        {/* Main loader on first page hit */}
        {loading && loadMore === "search" && (
          <Loading className="p-6" size={6}>
            Loading meetings...
          </Loading>
        )}
      </div>
    </div>
  );
};

export default MeetingOverview;
